import {
  AddOutlined as AddOutlinedIcon,
  AutoFixHighOutlined as AutoFixHighOutlinedIcon,
  DeleteOutlineOutlined as DeleteOutlineOutlinedIcon,
  PlaylistAddCheckCircleOutlined as PlaylistAddCheckCircleOutlinedIcon,
  SoupKitchenOutlined as SoupKitchenOutlinedIcon
} from '@mui/icons-material'
import Badge from '../../components/Badge'
import SectionCard from '../../components/SectionCard'
import {
  formatRecipeQuantityFromBase,
  getBaseUnitLabel,
  getDefaultRecipeDisplayUnit,
  getRecipeQuantityInputValue,
  getRecipeQuantityStoredHint,
  getRecipeQuantityUnitOptions
} from '../../utils/inventoryUnits'
import { getUnitContextText } from './recipeSection.utils'

const RecipeStepCard = ({ step, title, description, icon: IconComponent, children }) => (
  <div className='rounded-2xl border border-slate-200 bg-slate-50/80 p-4'>
    <div className='flex items-start gap-3'>
      <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-accent2/20 bg-accent2/[0.10] text-accent2'>
        <IconComponent style={{ fontSize: '1rem' }} />
      </div>
      <div className='min-w-0'>
        <p className='text-[11px] font-semibold uppercase tracking-[0.14em] text-accent2'>Paso {step}</p>
        <h4 className='mt-1 text-base font-semibold text-slate-900'>{title}</h4>
        <p className='mt-1 text-sm leading-5 text-slate-600'>{description}</p>
      </div>
    </div>
    <div className='mt-4'>{children}</div>
  </div>
)

const RecipeIngredientCard = ({ component, componentOptions, index, onChange, onRemove, canRemove }) => {
  const selectedOption =
    componentOptions.find(option => String(option.id) === String(component.componentItemId)) || null
  const ingredientUnit = selectedOption?.unit || ''
  const displayUnit = component.displayUnit || getDefaultRecipeDisplayUnit(ingredientUnit)
  const quantityUnitOptions = getRecipeQuantityUnitOptions(ingredientUnit)
  const quantityInputValue = getRecipeQuantityInputValue(component)
  const storedQuantityHint = getRecipeQuantityStoredHint(component.qtyRequired, displayUnit, ingredientUnit)

  return (
    <div className='rounded-2xl border border-slate-200 bg-white p-4 shadow-sm'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div className='flex items-center gap-3'>
          <span className='inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-slate-900 px-2 text-xs font-semibold text-white'>
            {index + 1}
          </span>
          <div>
            <div className='flex flex-wrap items-center gap-2'>
              <p className='text-sm font-semibold text-slate-900'>Ingrediente {index + 1}</p>
              {ingredientUnit && <Badge text={getBaseUnitLabel(ingredientUnit)} tone='blue' />}
            </div>
            <p className='text-xs text-slate-500'>Selecciona el item y la cantidad que consume la receta.</p>
          </div>
        </div>

        <button
          type='button'
          disabled={!canRemove}
          onClick={onRemove}
          className='inline-flex items-center gap-1 rounded-lg border border-red-200 px-2.5 py-1.5 text-xs font-medium text-red-700 transition hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50'
        >
          <DeleteOutlineOutlinedIcon style={{ fontSize: '0.9rem' }} />
          Quitar
        </button>
      </div>

      <div className='mt-4 grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_220px_140px]'>
        <div>
          <label className='mb-1 block text-xs font-semibold text-slate-700'>Ingrediente</label>
          <select
            value={component.componentItemId}
            onChange={event => onChange('componentItemId', event.target.value)}
            className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
          >
            <option value=''>Seleccionar ingrediente</option>
            {componentOptions.map(option => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className='mb-1 block text-xs font-semibold text-slate-700'>Cantidad necesaria</label>
          <input
            type='number'
            min='0.0001'
            step='0.0001'
            value={quantityInputValue}
            onChange={event => onChange('displayQuantity', event.target.value)}
            className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
            placeholder='Ej: 2'
          />
          <p className='mt-1 text-xs text-slate-500'>
            {ingredientUnit
              ? storedQuantityHint || `Esta cantidad se descontará en ${getUnitContextText(ingredientUnit)}.`
              : 'Selecciona un ingrediente para ver en qué unidad se descuenta.'}
          </p>
        </div>

        <div>
          <label className='mb-1 block text-xs font-semibold text-slate-700'>Unidad de ingreso</label>
          <select
            value={displayUnit}
            onChange={event => onChange('displayUnit', event.target.value)}
            disabled={!ingredientUnit || quantityUnitOptions.length <= 1}
            className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm disabled:bg-slate-50 disabled:text-slate-400'
          >
            {!ingredientUnit && <option value=''>Unidad</option>}
            {quantityUnitOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <p className='mt-1 text-xs text-slate-500'>
            {ingredientUnit
              ? `Puedes escribir la cantidad en ${quantityUnitOptions.map(option => option.label).join(' o ')}.`
              : 'La unidad aparecerá cuando selecciones un ingrediente.'}
          </p>
        </div>
      </div>

      <details className='mt-4 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2'>
        <summary className='cursor-pointer text-sm font-medium text-slate-700'>Opciones avanzadas</summary>
        <div className='mt-3 grid grid-cols-1 gap-3 md:grid-cols-2'>
          <div>
            <label className='mb-1 block text-xs font-semibold text-slate-700'>Merma % (opcional)</label>
            <input
              type='number'
              min='0'
              step='0.01'
              value={component.wastePct}
              onChange={event => onChange('wastePct', event.target.value)}
              className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
              placeholder='Ej: 5'
            />
          </div>
          <div>
            <label className='mb-1 block text-xs font-semibold text-slate-700'>Orden interno (opcional)</label>
            <input
              type='number'
              min='1'
              step='1'
              value={component.sortOrder}
              onChange={event => onChange('sortOrder', event.target.value)}
              className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
              placeholder={`Ej: ${index + 1}`}
            />
          </div>
        </div>
      </details>
    </div>
  )
}

const RecipeBuildSummary = ({
  selectedBomOption,
  ingredients,
  recipe,
  recipeForm,
  onOpenCurrentRecipe
}) => (
  <div className='rounded-2xl border border-slate-200 bg-slate-50 px-4 py-4'>
    <div className='flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between'>
      <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
        <div>
          <p className='text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500'>Producto</p>
          <p className='mt-1 text-sm font-semibold text-slate-900'>
            {selectedBomOption?.name || 'Aún no seleccionas un producto'}
          </p>
        </div>
        <div>
          <p className='text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500'>Rinde</p>
          <p className='mt-1 text-sm font-semibold text-slate-900'>
            {selectedBomOption
              ? formatRecipeQuantityFromBase(recipeForm.yieldQty, selectedBomOption.unit)
              : 'Pendiente'}
          </p>
        </div>
        <div>
          <p className='text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-500'>Ingredientes</p>
          <p className='mt-1 text-sm font-semibold text-slate-900'>{ingredients.length}</p>
        </div>
      </div>

      {recipe && (
        <button
          type='button'
          onClick={onOpenCurrentRecipe}
          className='inline-flex items-center justify-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-accent2/30 hover:bg-accent2/[0.05] hover:text-primary'
        >
          Ver receta actual
        </button>
      )}
    </div>
  </div>
)

const RecipeBuildView = ({
  actions,
  data,
  state,
  selectedBomOption,
  recipeDraftIngredients,
  finalProductUnitLabel,
  finalProductSingularUnitLabel,
  onBuilderProductChange,
  onIngredientFieldChange,
  onOpenCurrentRecipe
}) => (
  <SectionCard
    title='Construir receta'
    description='Crea o versiona una receta sin mezclar la edición con el listado completo.'
  >
    <div className='space-y-4'>
      <RecipeBuildSummary
        selectedBomOption={selectedBomOption}
        ingredients={recipeDraftIngredients}
        recipe={data.selectedRecipe}
        recipeForm={state.recipeForm}
        onOpenCurrentRecipe={onOpenCurrentRecipe}
      />

      <form className='space-y-4' onSubmit={actions.handleCreateRecipeSubmit}>
        <RecipeStepCard
          step='1'
          title='Producto a preparar'
          description='Selecciona el producto final que consumirá ingredientes cuando se produzca.'
          icon={PlaylistAddCheckCircleOutlinedIcon}
        >
          <label className='mb-1 block text-xs font-semibold text-slate-700'>Producto con receta</label>
          <select
            value={state.selectedBomItemId}
            onChange={onBuilderProductChange}
            className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
          >
            <option value=''>Seleccionar producto</option>
            {data.bomItemOptions.map(option => (
              <option key={option.id} value={option.id}>
                {option.label}
              </option>
            ))}
          </select>
          <p className='mt-2 text-xs text-slate-500'>
            Sólo aparecen productos configurados para trabajar con receta.
          </p>

          {selectedBomOption?.unit && (
            <div className='mt-3 rounded-xl border border-accent2/15 bg-accent2/[0.06] px-3 py-3'>
              <div className='flex flex-wrap items-center gap-2'>
                <Badge text={`Unidad final: ${finalProductUnitLabel}`} tone='blue' />
                {selectedBomOption.sku && <Badge text={`SKU: ${selectedBomOption.sku}`} tone='neutral' />}
              </div>
              <p className='mt-2 text-sm text-slate-700'>
                El rendimiento de la receta se expresará en <span className='font-semibold'>{finalProductUnitLabel}</span>,
                porque esa es la unidad del producto final.
              </p>
            </div>
          )}

          {data.selectedRecipe && (
            <div className='mt-3 flex flex-col gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-3 md:flex-row md:items-center md:justify-between'>
              <div className='text-sm text-amber-900'>
                Este producto ya tiene receta guardada. Si continúas aquí, prepararás una nueva versión.
              </div>
              <button
                type='button'
                onClick={onOpenCurrentRecipe}
                className='inline-flex items-center justify-center rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm font-medium text-amber-900 transition hover:bg-amber-100'
              >
                Ver receta actual
              </button>
            </div>
          )}
        </RecipeStepCard>

        <div className='grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_280px]'>
          <RecipeStepCard
            step='2'
            title='Ingredientes'
            description='Agrega uno o más ingredientes con la cantidad necesaria para producir la receta.'
            icon={SoupKitchenOutlinedIcon}
          >
            <div className='mb-4 flex flex-wrap items-center justify-between gap-3'>
              <div>
                <p className='text-sm font-semibold text-slate-900'>Ingredientes y cantidades</p>
                <p className='mt-1 text-sm text-slate-500'>
                  Ejemplo: cafe 20 gr, leche 300 ml o cemento 100 unidades.
                </p>
              </div>
              <button
                type='button'
                onClick={actions.handleAddRecipeComponent}
                className='inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-accent2/30 hover:bg-accent2/[0.05] hover:text-primary'
              >
                <AddOutlinedIcon style={{ fontSize: '1rem' }} />
                Agregar ingrediente
              </button>
            </div>

            <div className='space-y-3'>
              {state.recipeComponents.map((component, index) => (
                <RecipeIngredientCard
                  key={`recipe-component-${index}`}
                  component={component}
                  componentOptions={data.recipeComponentOptions}
                  index={index}
                  onChange={(field, value) => onIngredientFieldChange(index, field, value)}
                  onRemove={() => actions.handleRemoveRecipeComponent(index)}
                  canRemove={state.recipeComponents.length > 1}
                />
              ))}
            </div>
          </RecipeStepCard>

          <RecipeStepCard
            step='3'
            title='Resultado final'
            description='Define cuánto entrega esta receta usando la misma unidad del producto final.'
            icon={AutoFixHighOutlinedIcon}
          >
            <div>
              <label className='mb-1 block text-xs font-semibold text-slate-700'>
                Esta receta rinde{finalProductUnitLabel ? ` en ${finalProductUnitLabel}` : ''}
              </label>
              <input
                type='number'
                min='0.0001'
                step='0.0001'
                value={state.recipeForm.yieldQty}
                onChange={event => state.setRecipeForm(prev => ({ ...prev, yieldQty: event.target.value }))}
                className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                placeholder='Ej: 1'
              />
              <p className='mt-2 text-xs leading-4 text-slate-500'>
                Ejemplo: usa <span className='font-semibold'>1</span> si con estos ingredientes produces{' '}
                <span className='font-semibold'>1 {finalProductSingularUnitLabel}</span>.
              </p>
            </div>

            <label className='mt-4 flex items-start gap-2 rounded-xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700'>
              <input
                type='checkbox'
                checked={state.recipeForm.active}
                onChange={event => state.setRecipeForm(prev => ({ ...prev, active: event.target.checked }))}
                className='mt-0.5'
              />
              <span>
                <span className='block font-medium'>Receta activa</span>
                <span className='text-xs text-slate-500'>Si está activa, podrá usarse para descontar ingredientes.</span>
              </span>
            </label>

            <details className='mt-4 rounded-xl border border-slate-200 bg-white px-3 py-2'>
              <summary className='cursor-pointer text-sm font-medium text-slate-700'>Ajustes avanzados</summary>
              <div className='mt-3'>
                <label className='mb-1 block text-xs font-semibold text-slate-700'>Versión interna</label>
                <input
                  type='text'
                  value={state.recipeForm.version}
                  onChange={event => state.setRecipeForm(prev => ({ ...prev, version: event.target.value }))}
                  className='w-full rounded-lg border border-gray-300 px-3 py-2 text-sm'
                />
                <p className='mt-2 text-xs leading-4 text-slate-500'>
                  Déjalo en <span className='font-semibold'>1</span> si es la primera receta de este producto.
                </p>
              </div>
            </details>
          </RecipeStepCard>
        </div>

        <div className='flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-4 md:flex-row md:items-center md:justify-between'>
          <div>
            <p className='text-sm font-semibold text-slate-900'>Guardar receta</p>
            <p className='mt-1 text-sm text-slate-500'>
              Confirma la configuración para dejar esta versión disponible en inventario.
            </p>
          </div>
          <button
            type='submit'
            disabled={actions.createRecipeMutation.isPending}
            className='inline-flex items-center justify-center rounded-lg bg-[#f26c22] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#d95e1f] disabled:opacity-60'
          >
            {actions.createRecipeMutation.isPending ? 'Guardando...' : 'Guardar receta'}
          </button>
        </div>
      </form>
    </div>
  </SectionCard>
)

export default RecipeBuildView
